import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Trash2, MessageSquare, ChevronLeft, ChevronRight, Bot, MessageCircle, Mic, Square, Loader2, Volume2, Headphones } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChatMessage } from './ChatMessage';
import { LiveChatWidget } from './LiveChatWidget';
import { useAuth } from '../../context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

// Storage key for chat position
const CHAT_POSITION_KEY = 'kineserve-chat-position';
const CHAT_MODE_KEY = 'kineserve-chat-mode';

export const ChatPanel = ({ isOpen, onClose, conversationId: initialConversationId = null }) => {
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  
  // Close confirmation dialog state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  
  // Chat mode state: 'traditional' or 'conversational'
  const [chatMode, setChatMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CHAT_MODE_KEY) || null; // null = not yet selected
    }
    return null;
  });
  const [showModeSelector, setShowModeSelector] = useState(false);
  
  // Language preference state
  const CHAT_LANGUAGE_KEY = 'kineserve-chat-language';
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CHAT_LANGUAGE_KEY) || 'en';
    }
    return 'en';
  });
  const [showLanguageStep, setShowLanguageStep] = useState(false);
  
  // Supported languages
  const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Chat position state: 'right' or 'left'
  const [position, setPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CHAT_POSITION_KEY) || 'right';
    }
    return 'right';
  });
  
  // Live chat state
  const [showLiveChat, setShowLiveChat] = useState(false);

  // Toggle position and save preference
  const togglePosition = async () => {
    const newPosition = position === 'right' ? 'left' : 'right';
    setPosition(newPosition);
    localStorage.setItem(CHAT_POSITION_KEY, newPosition);
    
    // Save to user profile if logged in
    if (user?.id) {
      try {
        await api.post('/users/preferences', { chat_position: newPosition });
      } catch (error) {
        console.error('Failed to save chat position preference:', error);
      }
    }
  };

  // Load user preference on mount
  useEffect(() => {
    const loadUserPreference = async () => {
      if (user?.id) {
        try {
          const response = await api.get('/users/preferences');
          if (response.data?.chat_position) {
            setPosition(response.data.chat_position);
            localStorage.setItem(CHAT_POSITION_KEY, response.data.chat_position);
          }
        } catch (error) {
          // Use localStorage fallback
          console.log('Using localStorage for chat position');
        }
      }
    };
    loadUserPreference();
  }, [user?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize conversation when panel opens
  useEffect(() => {
    if (isOpen && !conversationId) {
      // For new conversations, always show mode selector
      // This allows users to change their mode/language preferences
      setShowModeSelector(true);
    } else if (isOpen && conversationId) {
      loadMessages();
    }
  }, [isOpen, conversationId]);

  // Handle mode selection
  const handleModeSelect = async (mode) => {
    setChatMode(mode);
    localStorage.setItem(CHAT_MODE_KEY, mode);
    
    if (mode === 'traditional') {
      // For traditional mode, show language selection step
      setShowModeSelector(false);
      setShowLanguageStep(true);
    } else {
      // For conversational mode, proceed directly (language will be auto-detected)
      setShowModeSelector(false);
      
      // Save to user profile if logged in
      if (user?.id) {
        try {
          await api.post('/users/preferences', { chat_mode: mode, chat_language: 'auto' });
        } catch (error) {
          console.error('Failed to save chat mode preference:', error);
        }
      }
      
      // Initialize conversation with auto language detection
      initializeConversation(mode, 'auto');
    }
  };
  
  // Handle language selection for traditional mode
  const handleLanguageSelect = async (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem(CHAT_LANGUAGE_KEY, langCode);
    setShowLanguageStep(false);
    
    // Save to user profile if logged in
    if (user?.id) {
      try {
        await api.post('/users/preferences', { chat_mode: chatMode, chat_language: langCode });
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
    
    // Initialize conversation with selected language
    initializeConversation(chatMode, langCode);
  };

  // Handle click outside - show confirmation
  const handleOverlayClick = () => {
    if (dontAskAgain || messages.length <= 1) {
      // If user said don't ask again or conversation just started, close directly
      onClose();
    } else {
      setShowCloseConfirm(true);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  // Reset idle timeout on any activity
  useEffect(() => {
    if (isOpen && conversationId) {
      resetIdleTimeout();
    }
    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [isOpen, conversationId, messages]);

  const resetIdleTimeout = () => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    // Set 5-minute timeout
    idleTimeoutRef.current = setTimeout(() => {
      handleIdleTimeout();
    }, 5 * 60 * 1000);
  };

  const handleIdleTimeout = async () => {
    if (conversationId) {
      // Add timeout message
      const timeoutMsg = {
        id: Date.now().toString(),
        sender: 'bot',
        message: 'I will be here if you need me later',
        type: 'text',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, timeoutMsg]);
      
      // Close panel after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  const initializeConversation = async (mode = chatMode, language = selectedLanguage) => {
    try {
      setLoading(true);
      const response = await api.createChatConversation(mode, language);
      setConversationId(response.data.id);
      await loadMessages(response.data.id);
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId = conversationId) => {
    try {
      const response = await api.getConversationMessages(convId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !conversationId || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI immediately
    const tempUserMsg = {
      id: Date.now().toString(),
      sender: 'user',
      message: userMessage,
      type: 'text',
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      setLoading(true);
      setIsTyping(true);
      
      // Send message and get bot responses
      const response = await api.sendChatMessage(conversationId, userMessage);
      
      setIsTyping(false);
      
      // Add bot responses to UI
      // Handle new response format from chat router
      const botResponses = response.data.map((resp, idx) => {
        // New format has user_message and assistant_message
        if (resp.assistant_message) {
          return {
            id: resp.assistant_message.id || `${Date.now()}-${idx}`,
            sender: 'bot',
            message: resp.assistant_message.content || resp.assistant_message.message || '',
            type: resp.assistant_message.type || 'text',
            options: resp.assistant_message.options,
            metadata: resp.assistant_message.metadata,
            created_at: resp.assistant_message.created_at || new Date().toISOString()
          };
        }
        // Old format fallback
        return {
          id: `${Date.now()}-${idx}`,
          sender: 'bot',
          message: resp.text || resp.content || resp.message || '',
          type: resp.type || 'text',
          options: resp.options,
          metadata: resp.metadata,
          created_at: new Date().toISOString()
        };
      });
      
      setMessages(prev => [...prev, ...botResponses]);
      
      // Reset idle timeout
      resetIdleTimeout();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setIsTyping(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = (option) => {
    // Handle both string labels and option objects
    const optionLabel = typeof option === 'string' ? option : option?.label || '';
    const optionId = typeof option === 'string' ? '' : option?.id || '';
    
    // Check if this is a "Talk to Support" or "Chat with Support Staff" option to open live chat
    const lowerLabel = optionLabel.toLowerCase();
    if (optionId === 'start_live_chat' || lowerLabel.includes('talk to support') || 
        lowerLabel.includes('chat with support staff') || lowerLabel.includes('chatear con soporte') ||
        lowerLabel.includes('hablar con soporte') || lowerLabel.includes('parler au support') || 
        lowerLabel.includes('mit dem support sprechen') || lowerLabel.includes('सहायता से बात')) {
      setShowLiveChat(true);
      return;
    }
    
    setInputMessage(optionLabel);
    // Auto-submit
    setTimeout(() => {
      const event = new Event('submit', { bubbles: true, cancelable: true });
      document.getElementById('chat-form')?.dispatchEvent(event);
    }, 100);
  };

  const handleNewConversation = async () => {
    setMessages([]);
    setConversationId(null);
    // Show mode selector for new conversations
    setShowModeSelector(true);
  };

  // Switch chat mode mid-conversation
  const handleSwitchMode = async () => {
    const newMode = chatMode === 'traditional' ? 'conversational' : 'traditional';
    setChatMode(newMode);
    localStorage.setItem(CHAT_MODE_KEY, newMode);
    
    // Notify backend of mode switch
    if (conversationId) {
      try {
        await api.post(`/chat/${conversationId}/switch-mode`, { mode: newMode });
        toast.success(`Switched to ${newMode === 'traditional' ? 'Menu-Driven' : 'Conversational'} mode`);
      } catch (error) {
        console.error('Failed to switch mode:', error);
      }
    }
  };

  // ==================== VOICE RECORDING FUNCTIONS ====================
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Transcribe the audio
        await transcribeAudio(audioBlob);
      };
      
      // Start recording
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            // Stop recording after 60 seconds
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
      toast.success('Recording started. Speak now...');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else {
        toast.error('Failed to start recording');
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Clear timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };
  
  const transcribeAudio = async (audioBlob) => {
    try {
      setIsTranscribing(true);
      toast.info('Transcribing your voice...');
      
      const response = await api.transcribeVoice(audioBlob, conversationId);
      
      if (response.data.success && response.data.text) {
        // Set the transcribed text as input message
        setInputMessage(response.data.text);
        toast.success('Voice transcribed successfully!');
        
        // Optionally auto-submit in conversational mode
        if (chatMode === 'conversational') {
          setTimeout(() => {
            const event = new Event('submit', { bubbles: true, cancelable: true });
            document.getElementById('chat-form')?.dispatchEvent(event);
          }, 500);
        }
      } else {
        toast.error('Could not transcribe audio. Please try again.');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Transcription failed. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };
  
  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  if (!isOpen) return null;

  // Dynamic positioning classes
  const panelPositionClass = position === 'right' 
    ? 'right-0 animate-in slide-in-from-right' 
    : 'left-0 animate-in slide-in-from-left';
  
  const borderClass = position === 'right' ? 'border-l' : 'border-r';

  return (
    <>
      {/* Close Confirmation Dialog */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent className="z-[60]">
          <AlertDialogHeader>
            <AlertDialogTitle>Close Chat Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to close this chat session? Your conversation will be saved and you can continue later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="dontAskAgain"
              checked={dontAskAgain}
              onChange={(e) => setDontAskAgain(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="dontAskAgain" className="text-sm text-muted-foreground">
              Don't ask again this session
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>No, continue chatting</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>Yes, close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mode Selector Dialog */}
      {showModeSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-background rounded-lg shadow-xl p-6 max-w-md mx-4 animate-in fade-in zoom-in">
            <div className="text-center mb-6">
              <img 
                src="/amie-icon.jpg" 
                alt="Amie" 
                className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-primary"
              />
              <h2 className="text-xl font-semibold">Hi, I'm Amie!</h2>
              <p className="text-muted-foreground text-sm mt-1">How would you like to interact with me?</p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleModeSelect('traditional')}
                className="w-full p-4 border rounded-lg hover:bg-accent transition-colors text-left flex items-start gap-3"
                data-testid="mode-traditional"
              >
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium">Menu-Driven (Traditional)</div>
                  <div className="text-sm text-muted-foreground">
                    Quick options with guided menus. Best for common tasks like reporting incidents or checking status.
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => handleModeSelect('conversational')}
                className="w-full p-4 border rounded-lg hover:bg-accent transition-colors text-left flex items-start gap-3"
                data-testid="mode-conversational"
              >
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bot className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium">Conversational (AI-Powered)</div>
                  <div className="text-sm text-muted-foreground">
                    Natural language chat. Just describe what you need and I'll understand and help you.
                  </div>
                </div>
              </button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center mt-4">
              You can switch modes anytime during our conversation
            </p>
          </div>
        </div>
      )}

      {/* Language Selector Dialog (for Traditional mode) */}
      {showLanguageStep && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-background rounded-lg shadow-xl p-6 max-w-md mx-4 animate-in fade-in zoom-in">
            <div className="text-center mb-6">
              <img 
                src="/amie-icon.jpg" 
                alt="Amie" 
                className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-primary"
              />
              <h2 className="text-xl font-semibold">Choose Your Language</h2>
              <p className="text-muted-foreground text-sm mt-1">I'll show menus and respond in your preferred language</p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`p-3 border rounded-lg hover:bg-accent transition-colors text-left flex items-center gap-2 ${
                    selectedLanguage === lang.code ? 'border-primary bg-primary/10' : ''
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className="font-medium text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => {
                setShowLanguageStep(false);
                setShowModeSelector(true);
              }}
              className="w-full mt-4 p-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to mode selection
            </button>
          </div>
        </div>
      )}

      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={handleOverlayClick}
      />
      
      {/* Chat Panel */}
      <div className={`fixed ${panelPositionClass} top-0 h-full w-[400px] bg-background ${borderClass} shadow-2xl z-50 flex flex-col duration-300`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground">
          {/* Position toggle arrow - Left side */}
          {position === 'right' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePosition}
              className="text-primary-foreground hover:bg-primary-foreground/20 mr-2"
              title="Move chat to left side"
              data-testid="chat-move-left"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          
          <div className="flex items-center gap-3 flex-1">
            <img 
              src="/amie-icon.jpg" 
              alt="Amie" 
              className="w-10 h-10 rounded-full border-2 border-white"
            />
            <div>
              <h2 className="font-semibold">Amie</h2>
              <p className="text-xs opacity-90 flex items-center gap-1">
                {chatMode === 'conversational' ? (
                  <>
                    <Bot className="h-3 w-3" /> Conversational Mode
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-3 w-3" /> Menu Mode
                  </>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Mode switch button */}
            {chatMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSwitchMode}
                className="text-primary-foreground hover:bg-primary-foreground/20"
                title={`Switch to ${chatMode === 'traditional' ? 'Conversational' : 'Menu-Driven'} mode`}
                data-testid="chat-switch-mode"
              >
                {chatMode === 'traditional' ? <Bot className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
              </Button>
            )}
            {/* Voice mode info button - shows tooltip explaining how to use TTS */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                toast.info('Click the speaker icon on any message to hear Amie read it aloud', {
                  duration: 4000
                });
              }}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              title="Voice mode: Click the speaker icon on any message to hear it"
              data-testid="voice-mode-info"
            >
              <Volume2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewConversation}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              title="New conversation"
            >
              <MessageCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOverlayClick()}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              title="Close chat"
            >
              <X className="h-5 w-5" />
            </Button>
            
            {/* Position toggle arrow - Right side */}
            {position === 'left' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={togglePosition}
                className="text-primary-foreground hover:bg-primary-foreground/20 ml-1"
                title="Move chat to right side"
                data-testid="chat-move-right"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            messages.map((message, index) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                onOptionClick={handleOptionClick}
              />
            ))
          )}
          
          {isTyping && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span>Amie is typing...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t">
          <form id="chat-form" onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={isRecording ? `Recording... ${recordingTime}s` : "Type your message..."}
              disabled={loading || !conversationId || isRecording || isTranscribing}
              className="flex-1"
            />
            
            {/* Voice Recording Button */}
            <Button
              type="button"
              variant={isRecording ? "destructive" : "outline"}
              size="icon"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading || !conversationId || isTranscribing}
              title={isRecording ? "Click to stop recording" : "Click to start voice recording"}
              data-testid="voice-record-btn"
              className={isRecording ? "animate-pulse bg-red-500 hover:bg-red-600" : ""}
            >
              {isTranscribing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isRecording ? (
                <Square className="h-4 w-4 fill-current" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
            
            <Button 
              type="submit" 
              disabled={!inputMessage.trim() || loading || !conversationId || isRecording || isTranscribing}
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
          
          {/* Recording indicator */}
          {isRecording && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-500">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span>Recording: {recordingTime}s (max 60s)</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Live Chat Widget */}
      {showLiveChat && (
        <LiveChatWidget 
          isOpen={showLiveChat} 
          onClose={() => setShowLiveChat(false)}
          onBackToAmie={() => setShowLiveChat(false)}
        />
      )}
    </>
  );
};